import { isArray, isObject } from '@utmost.co/utmost-design-system';
import {
  DocumentNode,
  FieldNode,
  InlineFragmentNode,
  OperationDefinitionNode,
  FragmentDefinitionNode,
  FragmentSpreadNode
} from 'graphql';

export function withTrackUnusedFields<T extends object>(
  target: T | undefined | null,
  documentNode: DocumentNode,
  onLogUnusedKeys: (operationName: string, unusedValueKeys: string[], unusedQueryKeys: string[]) => void
): T | undefined | null {
  if (!target) {
    return target;
  }

  let valueKeys: Set<string>;
  try {
    valueKeys = flatObjectKeys(structuredClone(target));
  } catch {
    valueKeys = flatObjectKeys(JSON.parse(JSON.stringify(target)));
  }

  const [queryKeys, operationName] = flatDocumentKeys(documentNode);
  const consumedKeys: Set<string> = new Set<string>();

  let keysComparatorTrigger = window.setTimeout(() => {
    keysComparatorHandler();
  }, 5000);

  function keysComparatorHandler() {
    const unusedValueKeys = Array.from(valueKeys).filter((x) => !consumedKeys.has(x));
    const unusedQueryKeys = Array.from(queryKeys).filter((x) => !consumedKeys.has(x));
    onLogUnusedKeys(operationName, unusedValueKeys, unusedQueryKeys);
  }

  function setConsumedKey(key: string) {
    consumedKeys.add(key);
    clearTimeout(keysComparatorTrigger);
    keysComparatorTrigger = window.setTimeout(() => {
      keysComparatorHandler();
    }, 5000);
  }

  const proxyCache = new WeakMap();
  return new Proxy(target, {
    get(value: Record<string | symbol, any>, property) {
      const item = value[property];
      if (typeof property === 'string') {
        // biome-ignore lint/suspicious/noPrototypeBuiltins: expected - low level functionality
        if (item && typeof item === 'object' && value.propertyIsEnumerable(property)) {
          if (proxyCache.has(item)) return proxyCache.get(item);
          const proxy = createSubProxy(item, property, setConsumedKey);
          proxyCache.set(item, proxy);
          return proxy;
        }
        // biome-ignore lint/suspicious/noPrototypeBuiltins: expected - low level functionality
        if (value.hasOwnProperty(property)) {
          setConsumedKey(property);
        }
      }
      return item;
    }
  }) as T;
}

function createSubProxy(
  target: object,
  parentPropertyKey: string | undefined | null,
  consumedKeyLogger: (key: string) => void
): object {
  const proxyCache = new WeakMap();
  return new Proxy(target, {
    get(value: Record<string | symbol, any>, property) {
      const item = value[property];
      if (typeof property === 'string') {
        const nextKey = getObjectKey(property, parentPropertyKey);
        // biome-ignore lint/suspicious/noPrototypeBuiltins: expected - low level functionality
        if (item && typeof item === 'object' && value.propertyIsEnumerable(property)) {
          if (proxyCache.has(item)) return proxyCache.get(item);
          const proxy = createSubProxy(item, nextKey, consumedKeyLogger);
          proxyCache.set(item, proxy);
          return proxy;
        }
        // biome-ignore lint/suspicious/noPrototypeBuiltins: expected - low level functionality
        if (value.hasOwnProperty(property)) {
          consumedKeyLogger(nextKey);
        } else if (parentPropertyKey) {
          // if property name is length, status, map etc it means we read parent object so mark it as consumed
          consumedKeyLogger(parentPropertyKey);
        }
      }
      return item;
    }
  });
}

export function flatObjectKeys(model: object, parentKey?: string, set?: Set<string>): Set<string> {
  const flatModel = set ?? new Set<string>();
  for (const key in model) {
    const nextKey = getObjectKey(key, parentKey);
    const value = (model as Record<string, any>)[key];
    if (isObject(value)) {
      flatObjectKeys(value, nextKey, flatModel);
    } else if (isArray(value)) {
      for (let i = 0; i < value.length; i++) {
        flatObjectKeys(value[i], getObjectKey(key, parentKey), flatModel);
      }
    } else {
      flatModel.add(nextKey);
    }
  }
  return flatModel;
}

export function flatDocumentKeys(documentNode: DocumentNode, set?: Set<string>): [Set<string>, operationName: string] {
  const flatModel = set ?? new Set<string>();
  const operation = documentNode.definitions.find(isOperationDefinitionNode);
  const fragments = documentNode.definitions.filter(isFragmentDefinitionNode);
  if (!operation) {
    return [flatModel, 'undefined'];
  }
  const operationName = operation.name?.value ?? 'noOperationName';
  setKeysFromDefinitionNode(operation, fragments, flatModel);
  return [flatModel, operationName];
}
type Node = OperationDefinitionNode | FragmentDefinitionNode | FieldNode | InlineFragmentNode | FragmentSpreadNode;

function setKeysFromDefinitionNode(
  node: Node,
  fragments: FragmentDefinitionNode[],
  setKeys: Set<string>,
  parentKey?: string
) {
  if ('selectionSet' in node && node.selectionSet?.selections?.length) {
    for (const selectionNode of node.selectionSet.selections) {
      const key = getDocumentKey(selectionNode, parentKey);
      if (isFragmentSpread(selectionNode)) {
        const fragmentName = getDocumentKey(selectionNode, undefined);
        const targetFragment = fragments.find((x) => x.name.value === fragmentName);
        if (targetFragment) {
          setKeysFromDefinitionNode(targetFragment, fragments, setKeys, parentKey);
        }
      } else if ('selectionSet' in selectionNode && selectionNode.selectionSet?.selections?.length) {
        setKeysFromDefinitionNode(selectionNode, fragments, setKeys, key);
      } else {
        setKeys.add(key);
      }
    }
  } else if (!isOperationDefinitionNode(node)) {
    setKeys.add(getDocumentKey(node, parentKey));
  }
}

function getDocumentKey(node: Node, parentKey: string | undefined | null): string {
  const key = ('name' in node ? node.name?.value : null) || 'unknown';
  return !parentKey ? key : `${parentKey}.${key}`;
}

function getObjectKey(key: string, parentKey: string | undefined | null): string {
  const isNumberLiteral = !Number.isNaN(Number(key));
  // if key is an index of an array - ignore it
  if (isNumberLiteral) {
    return parentKey ?? key;
  }
  return !parentKey ? key : `${parentKey}.${key}`;
}

function isOperationDefinitionNode(node: unknown): node is OperationDefinitionNode {
  return isObject(node) && (node as OperationDefinitionNode)?.kind === 'OperationDefinition';
}

function isFragmentDefinitionNode(node: unknown): node is FragmentDefinitionNode {
  return isObject(node) && (node as FragmentDefinitionNode)?.kind === 'FragmentDefinition';
}

function isFragmentSpread(node: unknown): node is FragmentSpreadNode {
  return isObject(node) && (node as FragmentSpreadNode)?.kind === 'FragmentSpread';
}
